import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";
import { fetchVacantHouseRate, VacantHouseRatePoint } from "src/site/estat";
import { ChartSpec, debounce, isSp, renderChart } from "src/site/recharts";

// Site::PrefectureArea::VacantHouseRate::Component の Stimulus コントローラ。
// landIeul: components/domainParts/VacantHouseRate/index.tsx の useEffect + LineChart。

class SitePrefectureAreaVacantHouseRateController extends Controller<HTMLElement> {
  static targets = ["graph", "descriptionIncrease", "descriptionDecrease"];
  static values = { areaCode: String };

  declare readonly graphTarget: HTMLElement;
  declare readonly descriptionIncreaseTarget: HTMLElement;
  declare readonly descriptionDecreaseTarget: HTMLElement;
  declare readonly areaCodeValue: string;

  private graphData: VacantHouseRatePoint[] = [];
  private onResize?: () => void;

  connect(): void {
    this.onResize = debounce(() => this.draw());
    window.addEventListener("resize", this.onResize);
    void this.load();
  }

  disconnect(): void {
    if (this.onResize) window.removeEventListener("resize", this.onResize);
  }

  private async load(): Promise<void> {
    const result = await fetchVacantHouseRate(this.areaCodeValue);
    if (!result) return;
    this.graphData = result.graphData;
    this.descriptionIncreaseTarget.hidden = !result.isIncrease;
    this.descriptionDecreaseTarget.hidden = result.isIncrease;
    this.draw();
  }

  private draw(): void {
    if (this.graphData.length === 0) return;
    renderChart(this.graphTarget, this.spec());
  }

  // landIeul: buildYticks()。min/max に ±3 して 3 本の目盛りを作る。
  private spec(): ChartSpec {
    const rates = this.graphData.map((e) => e.vacantHouseRate);
    const max = Math.ceil(Math.max(...rates)) + 3;
    const min = Math.floor(Math.min(...rates)) - 3;
    const sp = isSp();
    const tickFontSize = sp ? "10px" : "12px";
    return {
      kind: "line",
      data: this.graphData as unknown as ChartSpec["data"],
      xKey: "year",
      xPadding: { left: 20, right: 20 },
      xTickFontSize: tickFontSize,
      yTickFontSize: tickFontSize,
      yDomain: [min, max],
      yTicks: [min, (max + min) / 2, max],
      yUnit: "%",
      yLocaleString: true,
      curve: "monotone",
      legendWrapperStyle: {
        "font-size": sp ? "10px" : "15px",
        "margin-left": "20px",
        "line-height": "40px",
      },
      series: [
        {
          key: "vacantHouseRate",
          name: "空き家率",
          stroke: "#FF367F",
          fill: "#FF367F",
          strokeWidth: sp ? 2 : 3,
          unit: "%",
        },
      ],
      containerStyle: "width:100%;height:100%;min-width:0",
    };
  }
}

application.register(
  "site--prefecture-area--vacant-house-rate",
  SitePrefectureAreaVacantHouseRateController,
);
